import React, { useEffect, useState } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import LeaveCalendar from './components/LeaveCalendar';
import RequestsDashboard from './components/RequestsDashboard';
import PolicyConfiguration from './components/PolicyConfiguration';
import EmployeeBalances from './components/EmployeeBalances';
import RequestSubmissionForm from './components/RequestSubmissionForm';
import ApprovalWorkflow from './components/ApprovalWorkflow';
import TimeOffAnalytics from './components/TimeOffAnalytics';
import FilterPanel from '../analytics-dashboard/components/FilterPanel';

const TimeOffManagement = () => {
  const [activeTab, setActiveTab] = useState('calendar');
  const [selectedDateRange, setSelectedDateRange] = useState('30d');
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [filters, setFilters] = useState({
    employee: '',
    department: '',
    leaveType: '',
    status: ''
  });

  useEffect(() => {
    document.title = 'Time Off Management - Intime HR';
  }, []);

  // Key metrics for time off management
  const kpiStats = [
    { 
      title: 'Pending Requests', 
      value: '12', 
      change: '+3 today', 
      changeType: 'neutral', 
      icon: 'Clock',
      color: 'from-amber-500 to-orange-500',
      urgent: true
    },
    { 
      title: 'Approved This Month', 
      value: '47', 
      change: '+15%', 
      changeType: 'positive', 
      icon: 'CheckCircle',
      color: 'from-emerald-500 to-teal-500'
    },
    { 
      title: 'Team Availability', 
      value: '92%', 
      change: '+2%', 
      changeType: 'positive', 
      icon: 'Users',
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      title: 'Average Days Used', 
      value: '18.5', 
      change: '+1.2 days', 
      changeType: 'neutral', 
      icon: 'Calendar',
      color: 'from-purple-500 to-indigo-500'
    }
  ];

  const navigationTabs = [
    { id: 'calendar', label: 'Calendar View', icon: 'Calendar', description: 'Team availability & approved time off' },
    { id: 'requests', label: 'Requests', icon: 'FileText', description: 'Pending approvals & recent submissions', badge: '12' },
    { id: 'policies', label: 'Policies', icon: 'Settings', description: 'Configure leave types & accrual rules' },
    { id: 'balances', label: 'Employee Balances', icon: 'PieChart', description: 'Individual leave balances & usage' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3', description: 'Usage patterns & trends' }
  ];

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'calendar':
        return <LeaveCalendar filters={filters} dateRange={selectedDateRange} />;
      case 'requests':
        return <RequestsDashboard filters={filters} onShowForm={() => setShowRequestForm(true)} />;
      case 'policies':
        return <PolicyConfiguration />;
      case 'balances':
        return <EmployeeBalances filters={filters} />;
      case 'analytics':
        return <TimeOffAnalytics dateRange={selectedDateRange} />;
      default:
        return <LeaveCalendar filters={filters} dateRange={selectedDateRange} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
      {/* Enhanced background with professional gradients */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/60 via-purple-50/40 to-slate-50" />
        <div className="absolute top-0 right-0 h-96 w-96 rounded-full bg-gradient-to-br from-purple-400/10 to-indigo-400/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-gradient-to-br from-cyan-400/8 to-blue-400/8 blur-3xl" />
      </div>
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Breadcrumb />

          {/* Enhanced Page Header */}
          <div className="mb-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="space-y-3">
              <h1 className="text-4xl font-bold tracking-tight text-gray-900">
                <span className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-700 bg-clip-text text-transparent">
                  Time Off Management
                </span>
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl">
                Comprehensive leave tracking and approval workflows with policy enforcement and calendar integration for your entire organization.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowRequestForm(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <Icon name="Plus" size={18} />
                Submit Request
              </button>
              
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur border border-gray-200/60 text-gray-700 font-semibold rounded-xl shadow-sm hover:shadow-md transform hover:-translate-y-0.5 transition-all duration-200">
                <Icon name="Download" size={18} />
                Export Report
              </button>
            </div>
          </div>

          {/* KPI Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {kpiStats?.map((stat, i) => (
              <div
                key={i}
                className="group relative overflow-hidden rounded-2xl bg-white/70 backdrop-blur border border-white/60 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`absolute inset-0 opacity-5 bg-gradient-to-br ${stat?.color}`} />
                {stat?.urgent && (
                  <div className="absolute top-3 right-3">
                    <span className="flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                    </span>
                  </div>
                )}
                <div className="relative p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${stat?.color} shadow-lg`}>
                      <Icon name={stat?.icon} size={20} className="text-white" />
                    </div>
                    <div className="text-right">
                      <div className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
                        stat?.changeType === 'positive' ? 'bg-emerald-50 text-emerald-700' 
                        : stat?.changeType === 'negative'? 'bg-red-50 text-red-700' :'bg-gray-50 text-gray-700'
                      }`}>
                        {stat?.changeType === 'positive' && <Icon name="TrendingUp" size={12} />}
                        {stat?.changeType === 'negative' && <Icon name="TrendingDown" size={12} />}
                        {stat?.changeType === 'neutral' && <Icon name="Info" size={12} />}
                        {stat?.change}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-600">{stat?.title}</h3>
                    <p className="text-3xl font-bold text-gray-900">{stat?.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Tabs */}
          <div className="mb-8">
            <div className="border-b border-gray-200/60">
              <nav className="-mb-px flex space-x-1 overflow-x-auto">
                {navigationTabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`group relative whitespace-nowrap py-4 px-6 border-b-2 font-semibold text-sm transition-all duration-200 ${
                      activeTab === tab?.id
                        ? 'border-indigo-500 text-indigo-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon name={tab?.icon} size={16} />
                      <span>{tab?.label}</span>
                      {tab?.badge && (
                        <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-indigo-100 bg-indigo-600 rounded-full">
                          {tab?.badge}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-gray-500 group-hover:text-gray-600">
                      {tab?.description}
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Filter Panel - Only show for relevant tabs */}
          {['calendar', 'requests', 'balances']?.includes(activeTab) && (
            <div className="mb-6">
              <FilterPanel 
                filters={filters} 
                onFiltersChange={handleFilterChange}
                activeTab={activeTab}
              />
            </div>
          )}

          {/* Time Range Selector for Analytics */}
          {activeTab === 'analytics' && (
            <div className="mb-6 flex justify-end">
              <div className="inline-flex items-center rounded-2xl bg-white/80 backdrop-blur border border-gray-200/60 shadow-sm p-1.5">
                {[
                  { id: '30d', label: '30 Days' },
                  { id: '90d', label: '90 Days' },
                  { id: '1y', label: '1 Year' }
                ]?.map((opt) => (
                  <button
                    key={opt?.id}
                    onClick={() => setSelectedDateRange(opt?.id)}
                    className={`px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 ${
                      selectedDateRange === opt?.id
                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/25 transform scale-105'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {opt?.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div className="rounded-2xl bg-white/80 backdrop-blur border border-white/60 shadow-lg">
            {renderTabContent()}
          </div>

          {/* Request Submission Modal */}
          {showRequestForm && (
            <RequestSubmissionForm 
              onClose={() => setShowRequestForm(false)}
              onSubmit={(data) => {
                console.log('Submitting time off request:', data);
                setShowRequestForm(false);
              }}
            />
          )}

          {/* Approval Workflow - Always rendered for managers */}
          <ApprovalWorkflow />
        </div>
      </main>
    </div>
  );
};

export default TimeOffManagement;